import { useRef, useState } from "react";
import * as yup from 'yup';
import { Formik } from 'formik';
import { Button as BSButton , Col, FloatingLabel, Form, Row } from "react-bootstrap";
import { router, routes } from "../../app/routes";
import { ProviderModel, UserModel } from "../../data/entities";
import { showErrorMessage, showSuccessMessage } from "../../utils";
import { ProviderService, UserService } from "../../services";
import { usePageActions } from "../";
import { useAsync, useSetPageTitle } from "../../hooks";
import { Button, Card, IconAndText, IconDefinitions } from "../../components";
import { getUserInfo } from "../../app";
import { useSelector } from "react-redux";
import { useCalendarEvents } from "../calendar/hooks";
import { SyncCalendarModal } from "./components";

export function ProfileIndexPage() {

  const submitRef = useRef<HTMLButtonElement>(null);
  const [showState, setShowState] = useState<boolean>(false);
  const modalState = { value: showState, setter: setShowState };
  const userId = useSelector(getUserInfo())?.id;
  const { exportCalendar } = useCalendarEvents();
  const [ webCalendarLink, setWebCalendarLink ] = useState<string>("");
  const [ userProviders, setUserProviders ] = useState<ProviderModel[]>();
  const validationSchema = yup.object().shape({
    email: yup.string().email("Email must be valid").required("Email is required"),
    firstName: yup.string().required("First Name is required"),
    lastName: yup.string().required("Last Name is required"),
  });

  const providerService = new ProviderService();
  const userService = new UserService();

  const { value: user, } = useAsync(() => userService.get(userId ?? 0), [userId]);

  useSetPageTitle(UserService.getUserEmail(), []);

  usePageActions(
    <Button
      onClick={async () => {
        const link = await exportCalendar(userId!);
        const userProviders = await providerService.getProvidersByUserIdAsync();
        if (link) {
          setWebCalendarLink(link);
          setUserProviders(userProviders);
          setShowState(true);
        }
      }}
    >
      <IconAndText iconName={IconDefinitions.publish} text="Export My Calendar" />
    </Button>
    );

  return (
      <Card title="Update Your Profile">
        <Formik
          validationSchema={validationSchema}
          initialValues={{
            email: UserService.getUserEmail(),
            firstName: UserService.getFirstName(),
            lastName: UserService.getLastName(),
          }}
          onSubmit={async (values) => {
            const updatedProfileModel: UserModel = {
              ...user,
              id: UserService.getId(),
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              userName: UserService.getUserName()
            };
            try {

              const ok = await new UserService().updateProfile(updatedProfileModel);
              if (!ok) {
                showErrorMessage("Failed to update profile. Contact support if the problem persists.");
                return;
              }

              showSuccessMessage("Your profile was successfully updated.");
              router.navigate(routes.profile.index);
            }
            catch (error) {
              console.error("Error updating profile:", error);
              showErrorMessage("Something went wrong. Please try again.");
              return;
            }

          }}
        >
          {({ values, handleSubmit, handleChange, touched, errors }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col>
                    <FloatingLabel label="Username" className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder="Username"
                        name="username"
                        value={UserService.getDisplayName()}
                        disabled={true}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FloatingLabel label="Email" className="mb-3">
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={values.email}
                        onChange={handleChange}
                        isInvalid={touched.email && !!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <FloatingLabel label="First Name" className="mb-3">
                      <Form.Control
                        type="text"
                        name="firstName"
                        placeholder="First Name"
                        value={values.firstName}
                        onChange={handleChange}
                        isInvalid={touched.firstName && !!errors.firstName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.firstName}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <FloatingLabel label="Last Name" className="mb-3">
                      <Form.Control
                        type="text"
                        name="lastName"
                        placeholder="Last Name"
                        value={values.lastName}
                        onChange={handleChange}
                        isInvalid={touched.lastName && !!errors.lastName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.lastName}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Col>
                </Row>
                <Row>
                  <Col className="col-auto">
                    <BSButton ref={submitRef} type="submit" className="d-none" />
                    <Button onClick={() => { submitRef.current?.click()}}>
                      <IconAndText iconName={IconDefinitions.save} text="Save" />
                    </Button>
                  </Col>
                </Row>
              </Form>
            )
          }}
        </Formik>
        <SyncCalendarModal
          showState={modalState}
          link={webCalendarLink}
          userProviders={userProviders}
        />
      </Card>
  );
}

export default ProfileIndexPage;

